import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ACCENT_BLUE, EASE_OUT, MUTED_GRAY, NOPING_GREEN } from '../constants';

const POINTS = 80;
const W = 800;
const H = 120;

function generateLagPoints(seed: number) {
  const pts: number[] = [];
  for (let i = 0; i < POINTS; i += 1) {
    const base = 140 + Math.sin(i * 0.3 + seed) * 50;
    const spike = Math.sin(i * 1.7 + seed * 2) > 0.5 ? Math.abs(Math.sin(i * 3.7 + seed * 5.1)) * 80 : 0;
    pts.push(Math.min(250, base + spike + Math.sin(i * 0.8 + seed) * 30));
  }
  return pts;
}

function generateSmoothPoints(seed: number) {
  const pts: number[] = [];
  for (let i = 0; i < POINTS; i += 1) {
    const base = 3 + Math.sin(i * 0.4 + seed) * 1.8;
    const detail = Math.cos(i * 0.7 + seed * 1.3) * 0.8;
    pts.push(Math.max(1, Math.min(5, base + detail)));
  }
  return pts;
}

function pointsToPath(values: number[], maxVal: number) {
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * W;
      const y = H - (v / maxVal) * (H - 10) - 5;
      return `${x},${y}`;
    })
    .join(' ');
}

export function BeforeAfterComparison() {
  const { t } = useTranslation(['components/trial/lp/v2b']);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [dividerPos, setDividerPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 800);
    return () => clearInterval(interval);
  }, []);

  const lagPts = generateLagPoints(tick * 0.3);
  const smoothPts = generateSmoothPoints(tick * 0.5);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current || !dragging.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(10, Math.min(90, (x / rect.width) * 100));
    setDividerPos(pct);
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => handleMove(e.clientX);
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [handleMove]);

  const lagAvg = Math.round(lagPts.reduce((a, b) => a + b, 0) / lagPts.length);
  const smoothAvg = (smoothPts.reduce((a, b) => a + b, 0) / smoothPts.length).toFixed(1);

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE_OUT }}>
      <div ref={containerRef} className='relative touch-none select-none overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D12]' style={{ height: 400 }}>
        {/* Full-width animated graph background */}
        <div className='absolute inset-0'>
          <svg viewBox={`0 0 ${W} ${H}`} className='absolute bottom-0 start-0 w-full' style={{ height: '45%' }} preserveAspectRatio='none'>
            {[0.25, 0.5, 0.75].map((frac) => (
              <line key={frac} x1='0' y1={H * frac} x2={W} y2={H * frac} stroke='white' strokeOpacity='0.05' strokeDasharray='4 4' />
            ))}
            <g style={{ clipPath: `inset(0 ${100 - dividerPos}% 0 0)` }}>
              <polyline fill='none' stroke={ACCENT_BLUE} strokeWidth='2.5' strokeLinejoin='round' points={pointsToPath(lagPts, 260)} />
              <polyline fill={`${ACCENT_BLUE}15`} stroke='none' points={`0,${H} ${pointsToPath(lagPts, 260)} ${W},${H}`} />
            </g>
            <g style={{ clipPath: `inset(0 0 0 ${dividerPos}%)` }}>
              <polyline fill='none' stroke={NOPING_GREEN} strokeWidth='2.5' strokeLinejoin='round' points={pointsToPath(smoothPts, 8)} />
              <polyline fill={`${NOPING_GREEN}15`} stroke='none' points={`0,${H} ${pointsToPath(smoothPts, 8)} ${W},${H}`} />
            </g>
          </svg>
        </div>

        {/* Left overlay: Without NoPing */}
        <div className='absolute inset-0 flex flex-col p-6 md:p-8' style={{ clipPath: `inset(0 ${100 - dividerPos}% 0 0)` }}>
          <div className='from-[#2ECDFF]/8 pointer-events-none absolute inset-0 bg-gradient-to-br to-transparent' />
          <div className='relative z-10'>
            <div className='mb-3 flex items-center gap-2'>
              <div className='size-2.5 animate-pulse rounded-full bg-[#2ECDFF]' />
              <span className='text-xs font-semibold uppercase tracking-widest' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-jetbrains)' }}>
                {t('before_after.without_noping')}
              </span>
            </div>
            <div className='mb-5 text-3xl font-black md:text-4xl' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-bai)' }}>
              {t('before_after.lag_spikes')}
            </div>
            <div className='space-y-3'>
              <div className='flex items-baseline gap-3'>
                <span className='text-xs uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-jetbrains)' }}>
                  {t('before_after.avg_ping')}
                </span>
                <span className='text-2xl font-bold' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-jetbrains)' }}>
                  {lagAvg}ms
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-sm' style={{ color: ACCENT_BLUE }}>
                  {'\u26A0'}
                </span>
                <span className='text-sm' style={{ color: `${ACCENT_BLUE}90`, fontFamily: 'var(--font-outfit)' }}>
                  {t('before_after.packet_loss_bad')}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-sm' style={{ color: ACCENT_BLUE }}>
                  {'\u26A0'}
                </span>
                <span className='text-sm' style={{ color: `${ACCENT_BLUE}90`, fontFamily: 'var(--font-outfit)' }}>
                  {t('before_after.jitter_bad')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right overlay: With NoPing */}
        <div className='absolute inset-0 flex flex-col p-6 md:p-8' style={{ clipPath: `inset(0 0 0 ${dividerPos}%)` }}>
          <div className='pointer-events-none absolute inset-0 bg-gradient-to-bl from-[#93FF18]/5 to-transparent' />
          <div className='relative z-10 text-end'>
            <div className='mb-3 flex items-center justify-end gap-2'>
              <span className='text-xs font-semibold uppercase tracking-widest' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
                {t('before_after.with_noping')}
              </span>
              <div className='size-2.5 rounded-full bg-[#93FF18]' />
            </div>
            <div className='mb-5 text-3xl font-black md:text-4xl' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
              {t('before_after.buttery_smooth')}
            </div>
            <div className='space-y-3'>
              <div className='flex items-center justify-end gap-3'>
                <span className='text-xs uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-jetbrains)' }}>
                  {t('before_after.avg_ping')}
                </span>
                <span className='text-2xl font-bold' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
                  {smoothAvg}ms
                </span>
              </div>
              <div className='flex items-center justify-end gap-2'>
                <span className='text-sm' style={{ color: `${NOPING_GREEN}90`, fontFamily: 'var(--font-outfit)' }}>
                  {t('before_after.packet_loss_good')}
                </span>
                <span className='text-sm' style={{ color: NOPING_GREEN }}>
                  {'\u2713'}
                </span>
              </div>
              <div className='flex items-center justify-end gap-2'>
                <span className='text-sm' style={{ color: `${NOPING_GREEN}90`, fontFamily: 'var(--font-outfit)' }}>
                  {t('before_after.jitter_good')}
                </span>
                <span className='text-sm' style={{ color: NOPING_GREEN }}>
                  {'\u2713'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Draggable divider */}
        <div
          className='absolute inset-y-0 z-30 flex cursor-col-resize items-center justify-center'
          style={{ left: `${dividerPos}%`, transform: 'translateX(-50%)', width: 44 }}
          onPointerDown={() => {
            dragging.current = true;
          }}
        >
          <div className='h-full w-px bg-white/40' />
          <div className='absolute flex size-11 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 shadow-lg backdrop-blur-md'>
            <svg width='18' height='18' viewBox='0 0 16 16' fill='none'>
              <path d='M5 3L2 8L5 13' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
              <path d='M11 3L14 8L11 13' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </div>
        </div>

        {/* Drag hint */}
        <div className='absolute bottom-3 left-1/2 z-20 -translate-x-1/2'>
          <span className='rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-widest' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-jetbrains)' }}>
            {t('before_after.drag_hint')}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
